import React, { useState } from 'react'

/**
 * AbwaabPlus Input — single-line text field. 16px text on mobile (prevents iOS
 * zoom). Force dir="ltr" for phone / OTP / access-key inputs. Search inputs put
 * the magnifier on the trailing edge via `leadingIcon`.
 *
 * Forwards its ref + spreads native props so it drops into react-hook-form
 * `register(...)` and existing controlled inputs unchanged.
 */
export interface AbwInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	label?: React.ReactNode
	error?: React.ReactNode
	leadingIcon?: React.ReactNode
	/** OTP / code style: 24px bold, centered, letter-spaced. */
	otp?: boolean
	containerStyle?: React.CSSProperties
}

export const Input = React.forwardRef<HTMLInputElement, AbwInputProps>(function Input(
	{ label, error, leadingIcon, otp = false, style, containerStyle, onFocus, onBlur, ...rest },
	ref,
) {
	const [focus, setFocus] = useState(false)
	const invalid = !!error
	return (
		<label style={{ display: 'block', ...containerStyle }}>
			{label && (
				<span style={{ display: 'block', font: 'var(--text-label)', color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</span>
			)}
			<span style={{ position: 'relative', display: 'block' }}>
				{leadingIcon && (
					<span
						style={{
							position: 'absolute',
							insetInlineEnd: 12,
							top: '50%',
							transform: 'translateY(-50%)',
							color: 'var(--text-secondary)',
							display: 'flex',
						}}
					>
						{leadingIcon}
					</span>
				)}
				<input
					ref={ref}
					onFocus={e => {
						setFocus(true)
						onFocus?.(e)
					}}
					onBlur={e => {
						setFocus(false)
						onBlur?.(e)
					}}
					style={{
						width: '100%',
						height: 36,
						border: `1px solid ${invalid ? 'var(--error)' : focus ? 'var(--blue)' : 'var(--border)'}`,
						borderRadius: 'var(--r-md)',
						padding: leadingIcon ? '0 36px 0 12px' : '0 12px',
						font: `var(--fw-${otp ? 'bold' : 'regular'}) ${otp ? 24 : 16}px var(--font-sans)`,
						background: 'var(--surface-card)',
						color: 'var(--text-primary)',
						outline: 'none',
						boxShadow: focus && !invalid ? '0 0 0 3px rgba(6,85,203,0.5)' : invalid && focus ? '0 0 0 3px rgba(239,68,68,0.4)' : 'none',
						transition: 'border-color var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease)',
						textAlign: otp ? 'center' : 'start',
						letterSpacing: otp ? '0.5em' : 'normal',
						...style,
					}}
					{...rest}
				/>
			</span>
			{invalid && <div style={{ font: 'var(--text-label)', color: 'var(--error)', marginTop: 4 }}>{error}</div>}
		</label>
	)
})
